import { defineStore } from 'pinia';
import { reactive, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import * as yup from 'yup';

import { totals } from '@/data';
import i18n from '@/i18n';
import { useToastStore } from '@/stores/toastStore';
import type { APIReceipt, BasketData, Cardfee, CartItem, CustomField, CustomFieldData, CustomFieldOrderData, CustomFieldOrderDataEntry, DifferentCountMessage,FieldListItem, Giftcard, Order, OrderData, PassbookData,ReceiptConfirmPayment, SimpleBasketData, StartPurchaseData, Ticket, Totals, UnpaidOrder } from '@/types';
import { trackAddToCart, trackRemoveFromCart } from '@/utils/analytics/ecommerce';
import { mapDtoToCartItem } from '@/utils/dtoTransformers/cartItemDTO';
import { eventToCartItem } from '@/utils/eventToCartItem';
import { normalizeUnixTimestamp } from '@/utils/formatDate';
import { isEmbed } from '@/utils/isEmbed';
import { slugify } from '@/utils/slugify';
import { useWebsocketStore } from '@/websocket/websocket';

import { useCheckoutStore } from './checkoutStore';
import { useDoorsaleStore } from './doorsaleStore';
import { useGeneralStore } from './general';
import { useSeatmapStore } from './seatmapStore';

export const useCartStore = defineStore('cart', () => {
  const websocketStore = useWebsocketStore();
  let basketData: BasketData = {
    basket: [],
    cardtypes: [],
    dankort_discount: 0,
    dankort_discount_max: 0,
    dankort_discount_min: 0,
    error: '',
    different_count_message: undefined,
    expire: 0,
    volume_discounts_in_use: undefined,
    customfields: undefined,
    timeout: undefined,
  };
  const cartExpire = ref<number>(0);
  let cartExpireInterval: number | undefined = undefined;

  const emptyPassbookData: PassbookData = {
    link: '',
    qr_code: '',
    atpblink: '',
  };

  const emptyReceiptConfirmPayment: ReceiptConfirmPayment = {
    cardnumber: '',
    currency: '',
    fee: 0,
    giftcards: [],
    mobile_pay_info: '',
    ok: false,
    orderid: 0,
    transaction: '',
    ts: 0,
    ts_gmt_offset: 0,
    passbook: emptyPassbookData,
  };

  const emptyReceiptOrder: Order = {
      status: '',
      orderid: 0,
      ts: 0,
      ts_gmt_offset: 0,
      mailid: '',
      basket: [],
      info: {} as OrderData,
      payment_id: '',
      payment_vars: {},
      dankort_discount: 0,
      giftcards: [],
      additional_receipt_message: '',
      hide_receipt_download_button: true,
  };

  const emptyOrderData: OrderData = {
    email: '',
    email2: '',
    name: '',
    address: '',
    address2: '',
    zip: '',
    city: '',
    telephone: '',
    country: '',
    newsletter: false,
    customcheckbox: false,
    vouchercodesavedata: false,
    mailshipping: false,
    giftcards: [], // change later
    price_group_info: undefined, // change later
    customfields_data: undefined, // change later
  };

  const emptyCustomFieldData: CustomFieldData = {
        custom_fields: [],
        custom_fields_perticket: [],
        custom_fields_pertickettype: [],
        fieldlists: {},
  };

  const emptyCardfee: Cardfee = {
    dankort_discount: null,
    min: 0,
    max: 0,
    mintype: '',
  };

  const emptyUnpaidOrder: UnpaidOrder = {
    cardfee: emptyCardfee,
    order: emptyReceiptOrder,
    psp: '',
    psp_warning: '',
  };

  const cartTotals = ref<Totals>(totals);
  const cardTypes = ref<string[]>([]);
  const receiptCart = ref<boolean>(false);
  const receiptError = ref<string | null>(null);
  const paymentConfirmed = ref<boolean>(false);
  const pdfReady = ref<boolean>(false);
  const receiptOrder = ref<Order>(emptyReceiptOrder);
  const ReceiptConfirmPayment = ref<ReceiptConfirmPayment>(emptyReceiptConfirmPayment);
  const ReceiptConfirmPaymentsSent = ref<Record<string, boolean>>({});
  const pdfLink = ref<string>('#');
  const customfields = ref<CustomFieldData>(emptyCustomFieldData);

  const generalStore = useGeneralStore();
  const checkoutStore = useCheckoutStore();
  const doorsaleStore = useDoorsaleStore();
  const seatmapStore = useSeatmapStore();
  const items: CartItem[] = reactive(basketData.basket);
  const currentOrderData = ref<OrderData>(emptyOrderData);
  const customFieldRules = ref<Record<string, yup.AnySchema | undefined>>({});
  const currentCount = ref<Record<string, number>>({});
  const differentCount = ref<boolean>(false);
  const differentCountMessage = ref<DifferentCountMessage>({ message: 'initial', count: 0 });
  const unpaidOrder = ref<UnpaidOrder>(emptyUnpaidOrder);
  const unpaidOrderBackup = ref<UnpaidOrder>(emptyUnpaidOrder);
  const paymentFailedTimeoutFlag = ref<boolean>(false);
  const ignoreCancelPaymentModal = ref<boolean>(false);

  const paymentFailedTimeout = ref<number | undefined>(undefined);
  const paymentFailedExpire = ref<number>(0);
  const paymentFailedCart = ref<boolean>(false);
  const paymentFailedAvailable = ref<boolean>(false);
  const firstBasketLoad = ref<boolean>(true);

  const seatmapActive = ref<boolean>(false);

  // Confirmed per-ticket price totals (in cents) after seat selection with price groups.
  // Key: ticketId → sum of price-group prices for all confirmed seats of that ticket type.
  // Populated by applySeatsToCartItems; cleared when the user changes ticket counts.
  const confirmedSeatPrices = ref<Record<number, number>>({});

  // --- Giftcard state ---
  const GIFTCARD_SESSION_KEY = 'checkout-giftcards';
  const giftcards = ref<Giftcard[]>([]);

  function loadGiftcards() {
    try {
      const stored = sessionStorage.getItem(GIFTCARD_SESSION_KEY);
      if (stored) {
        giftcards.value = JSON.parse(stored);
      }
    } catch (e) {
      console.error('Error loading giftcards from sessionStorage:', e);
    }
  }

  function saveGiftcards() {
    sessionStorage.setItem(GIFTCARD_SESSION_KEY, JSON.stringify(giftcards.value));
  }

  function addGiftcard(data: Giftcard) {
    data.original_balance = data.balance;
    giftcards.value.push(data);
    saveGiftcards();
    calculateTotals();
  }

  function removeGiftcard(id: number) {
    giftcards.value = giftcards.value.filter(gc => gc.id !== id);
    saveGiftcards();
    calculateTotals();
  }

  function removeAllGiftcards() {
    giftcards.value = [];
    saveGiftcards();
  }

  function giftcardAlreadyAdded(code: string): boolean {
    const upperCode = code.toUpperCase();
    return giftcards.value.some(gc => gc.code === upperCode);
  }

  function getGiftcardVat(): number | null {
    let firstVat: number | null = null;
    for (const item of items) {
      let count = 0;
      for (const ticket of item.tickets) {
        count += ticket.count;
      }
      if (count > 0) {
        if (firstVat === null) {
          firstVat = item.vat.ticketprice;
        }
        if (item.vat.ticketprice !== firstVat) {
          return null;
        }
      }
    }
    return firstVat;
  }

  function giftcardAllowed(): boolean {
    let firstVat: number | null = null;
    let firstCurrency: string | null = null;
    for (const item of items) {
      let count = 0;
      for (const ticket of item.tickets) {
        count += ticket.count;
      }
      if (count > 0) {
        if (!item.giftcard_allowed) {
          return false;
        }
        if (firstVat === null) {
          firstVat = item.vat.ticketprice;
        }
        if (item.vat.ticketprice !== firstVat) {
          return false;
        }
        if (firstCurrency === null) {
          firstCurrency = item.currency;
        } else if (item.currency !== firstCurrency) {
          return false;
        }
      }
    }
    return true;
  }

  function getGiftcardInfo(): { code: string; id: number; balance: number }[] {
    return giftcards.value.map(gc => ({
      code: gc.code,
      id: gc.id,
      balance: gc.balance,
    }));
  }

  // --- Ticket selection queue ---
  // Stores the latest desired count per ticket while a reservation request is in flight.
  // Key: ticketId → { eventId, count }. Only the most recent change per ticket is kept.
  const pendingChanges = ref<Record<number, { eventId: number | string | undefined; count: number }>>({});
  // True while we are waiting for a websocket response to a reservetickets message.
  const reservationInFlight = ref<boolean>(false);
  // Non-reactive timer handle for the debounce flush.
  let flushTimer: number | undefined = undefined;

  /**
   * Queue a ticket count change. The latest count per ticket always wins.
   * A short debounce groups rapid clicks into one websocket message.
   * If a reservation is already in flight the change stays queued and will
   * be flushed automatically when the response arrives.
   */
  function queueTicketChange(
    eventId: number | string | undefined,
    ticketId: number,
    count: number,
  ) {
    pendingChanges.value[ticketId] = { eventId, count };
    clearTimeout(flushTimer);
    flushTimer = window.setTimeout(() => {
      if (!reservationInFlight.value) {
        flushPendingChanges();
      }
      // else: changes stay queued; updateReservedTickets will flush after response
    }, 300);
  }

  /**
   * Send a single reservetickets message covering every ticket with a non-zero
   * count (current basket + any pending overrides).
   */
  function flushPendingChanges() {
    const snapshot = { ...pendingChanges.value };
    pendingChanges.value = {};
    clearTimeout(flushTimer);

    if (Object.keys(snapshot).length == 0) return;

    reservationInFlight.value = true;

    // Invalidate seat prices / seatmap state (same as selectTickets)
    confirmedSeatPrices.value = {};
    seatmapStore.clearSeatmapState();

    // Apply pending counts to the local items array
    const reserveTickets: Record<number, number> = {};

    items.forEach(item => {
      item.tickets.forEach(ticket => {
        if (snapshot[ticket.id] != undefined) {
          ticket.count = snapshot[ticket.id].count;
          delete snapshot[ticket.id]; // handled
        }
        if (ticket.count > 0) {
          reserveTickets[ticket.id] = ticket.count;
        }
      });
    });

    // Any remaining entries in snapshot are tickets not yet in the basket
    for (const ticketId in snapshot) {
      const entry = snapshot[ticketId];
      if (entry.count > 0) {
        reserveTickets[Number(ticketId)] = entry.count;
      }
    }

    // Remove zero counts
    for (const ticketId in reserveTickets) {
      if (reserveTickets[ticketId] == 0) {
        delete reserveTickets[ticketId];
      }
    }

    if (Object.keys(reserveTickets).length > 0) {
      const olCustomer = checkoutStore.getUrlParam('OL_Customer');
      websocketStore.send('reservetickets', { tickets: reserveTickets, is_doorsale: doorsaleStore.isActive, order_limit_customer_id: olCustomer }, undefined);
    } else {
      reservationInFlight.value = false;
      emptyCart(true);
    }
  }

  function doBackupUnpaidOrder() {
    if( unpaidOrderBackup.value.order && unpaidOrderBackup.value.order.ts && unpaidOrderBackup.value.order.ts > 0 ) {
      const now = Date.now() / 1000;
      const orderExpireTime = unpaidOrderBackup.value.order.ts + (8 * 60);
      if( orderExpireTime > now ) {
        setUnpaidOrder(unpaidOrderBackup.value);
      }
      else {
        doHardLeaveUnpaidOrder();
      }
    }
  }

  function navigateAfterBasketTimeout() {
    if (isEmbed()) {
      router.replace({ name: 'checkout-timeout' });
      return;
    }
    router.push(doorsaleStore.isActive ? { name: 'doorsale-events' } : '/');
  }

  function setUnpaidOrder(data: UnpaidOrder) {
    //If we are not in checkout-payment, we do some cleanup and return
    if(router.currentRoute.value.name != 'checkout-payment' && router.currentRoute.value.name != 'checkout-information') {
      console.log('Not in checkout-payment or checkout-information, clearing unpaid order data and returning');
      paymentFailedAvailable.value = false;
      unpaidOrderBackup.value = emptyUnpaidOrder;
      clearUnpaidOrderData();
      return;
    }
    else if( data.order && data.order.ts){
      console.log('Setting unpaid order with timestamp:', data.order.ts);
      const now = Date.now() / 1000;
      const orderExpireTime = data.order.ts + (8 * 60);
      //If the order is already expired we go to the frontpage and clear cart and unpaid order data
      if( orderExpireTime < now ) {
        doHardLeaveUnpaidOrder();
      }
      else if(router.currentRoute.value.name == 'checkout-information'){
        //This usually happens when the user clicks the back button in their broswer from payment. So here we wish to set a flag.
        //The above if ensures the order is valid
        paymentFailedAvailable.value = true;
        unpaidOrderBackup.value = data;
        clearUnpaidOrderData();
        return;
      }
    }


    unpaidOrder.value = data;
    console.log('Unpaid order set:', unpaidOrder.value);
    if( data.order && data.order.orderid && data.order.status && data.order.status == 'placed' ) {
      checkoutStore.placeOrderPaymentVars = data.order.payment_vars;
    }
    // Set unpaid order timeout
    if(data.order && data.order.basket && data.order.basket.some(item => item.tickets.some(ticket => ticket.count > 0)) ) {
      console.log('Current time:', Date.now() / 1000);
      console.log('Unpaid order data:', data);


      basketData.basket = unpaidOrder.value.order.basket.map(item => mapDtoToCartItem(item as any));
      paymentFailedCart.value = true;
      addEventDataToBasket();
      items.splice(0, items.length, ...basketData.basket);
      console.log('Items after update:', items);
      calculateTotals();

      paymentFailedExpire.value = data.order.ts + (8 * 60);
      console.log('Payment failed expire time set to:', paymentFailedExpire.value, 'which is', new Date(paymentFailedExpire.value * 1000).toLocaleString());
      paymentFailedTimeout.value = window.setInterval(() => {
        const now = Date.now() / 1000;
        const orderExpireTime = paymentFailedExpire.value;
        if (orderExpireTime && orderExpireTime > 0 && now > orderExpireTime) {
          expireUnpaidOrder();
        }
      }, 1000);
    }
    else {
      paymentFailedTimeoutFlag.value = false;
      if( paymentFailedTimeout.value ) {
        clearInterval(paymentFailedTimeout.value);
        paymentFailedTimeout.value = undefined;
      }
    }

    //We shouldn't stay here if do not have an unpaid order or any valid as a backup
    if(
      router.currentRoute.value.name == 'checkout-payment' &&
      unpaidOrder.value.order == null &&
      !(unpaidOrderBackup.value.order && unpaidOrderBackup.value.order.ts && unpaidOrderBackup.value.order.ts > 0)
    ){
      doHardLeaveUnpaidOrder();
    }

  }


  function doHardLeaveUnpaidOrder() {
    console.log('Unpaid order expired, clearing unpaid order data and redirecting to frontpage');
    localStorage.setItem('showUnpaidOrderExpiredToast', 'true');
    resetCart();
    clearUnpaidOrderData(false);
    ignoreCancelPaymentModal.value = true;
    paymentFailedAvailable.value = false;
    paymentFailedCart.value = false;
    unpaidOrderBackup.value = emptyUnpaidOrder;
    navigateAfterBasketTimeout();
  }

  function expireUnpaidOrder() {
    console.log('Unpaid order expired, resetting cart and unpaid order data');
    resetCart();
    clearUnpaidOrderData(true);
  }

  function cancelUnpaidOrder() {
    resetCart();
    clearUnpaidOrderData(false);
    websocketStore.close();
  }

  function clearUnpaidOrderData(timeout: boolean = false) {
    paymentFailedTimeoutFlag.value = timeout;
    clearInterval(paymentFailedTimeout.value);
    paymentFailedTimeout.value = undefined;
    paymentFailedExpire.value = 0;
    paymentFailedCart.value = false;
    unpaidOrder.value = emptyUnpaidOrder;
    checkoutStore.placeOrderPaymentVars = null;

  }

  function setCustomFieldRule(fieldName: string, rule: yup.AnySchema | undefined) {
    customFieldRules.value[fieldName] = rule;
    // console.log('Custom field rule set for', fieldName, ':', rule);
  }

  function getCustomFieldRule(fieldName: string) {
    // console.log('Getting custom field rule for', fieldName);
    // console.log('Current custom field rules:', customFieldRules.value);
    return customFieldRules.value[fieldName];
  }

  function setCustomFieldData(basket: BasketData) {
    const data = basket.customfields as CustomFieldData | undefined;
    // console.log('Setting custom field data:', data);
    if( data ) {
      if(data.custom_fields != undefined){
        customfields.value.custom_fields = data.custom_fields;
      }
      if(data.custom_fields_perticket != undefined){
        customfields.value.custom_fields_perticket = data.custom_fields_perticket;
      }
      if(data.custom_fields_pertickettype != undefined){
        customfields.value.custom_fields_pertickettype = data.custom_fields_pertickettype;
      }
      if(data.fieldlists != undefined){
        customfields.value.fieldlists = data.fieldlists;
      }
      const tempOrderData = currentOrderData.value;
      const tempCustomFieldOrderData: CustomFieldOrderData = {};
      //Next iterate through customfields, customfields_perticket and customfields_pertickettype to setup currentOrderData.customfields_data
      //Afterwards crosscheck with existing currentOrderData.customfields_data to retain existing values
      //Custom fields
      customfields.value.custom_fields.forEach(field => {
        const customFieldType = getCustomFieldFieldType(field, null, customfields.value.fieldlists || {});
        const fieldListID = getFieldlistID(field, null);
        const tempCustomField: CustomFieldOrderDataEntry = {
            eventid: 'per_event',
            value: '',
            'must-be-checked': field.must_be_checked,
            fieldName: field.name,
            name: field.name,
            type: 'per_event',
            fieldType: customFieldType,
            fieldlistID: fieldListID ?? null,
            grouping: 'event'
        };
        if( tempOrderData.customfields_data && tempOrderData.customfields_data[tempCustomField.eventid] && tempOrderData.customfields_data[tempCustomField.eventid][field.name] ) {
          console.log('Retaining value for custom field', field.name, ':', tempOrderData.customfields_data[tempCustomField.eventid][field.name].value);
          tempCustomField.value = tempOrderData.customfields_data[tempCustomField.eventid][field.name].value;
          console.log('Value for custom field', field.name, ':', tempCustomField.value);
        }
        if(tempCustomField.value == '' && customFieldType == 'checkbox'){
          tempCustomField.value = false;
        }
        if( !tempCustomFieldOrderData[tempCustomField.eventid] ) {
          tempCustomFieldOrderData[tempCustomField.eventid] = {};
        }
        console.log('Setting custom field for event', tempCustomField.eventid, 'with name', field.name, ':', tempCustomField);
        tempCustomFieldOrderData[tempCustomField.eventid][field.name] = tempCustomField;
      });
      //For each event in basket we must setup per ticket and per tickettype customfields
      // console.log('Basket for custom fields:', basket.basket);
      const customfields_ticket_count: Record<string, number> = {};
      basket.basket.forEach(basketItem => {
        //... and now through tickets
        // console.log('Basket item for custom fields:', basketItem);
        basketItem.tickets.forEach(ticket => {
          //Per tickettype fields
          customfields.value.custom_fields_pertickettype.forEach(field => {
            console.log("----------------- customfields.value.custom_fields_pertickettype field -------------------", field);

            if( !field.event_id || field.event_id.toString() != basketItem.id.toString() ) {
              return;
            }
            if(field.tickets && field.tickets.length > 0) {
              const tempTicketId = field.tickets.find((id: number) => id == ticket.id); // for some reason include did not work.
              if(!tempTicketId) {
                return;
              }
            }
            const tempCustomFieldName = 'TT' + ticket.id + '_' + field.name;
            const customFieldType = getCustomFieldFieldType(field, ticket, customfields.value.fieldlists || {});
            const fieldListID = getFieldlistID(field, ticket);
            const tempCustomField: CustomFieldOrderDataEntry = {
                eventid: field.event_id.toString(),
                ticketID: ticket.id,
                value: '',
                'must-be-checked': field.must_be_checked,
                fieldName: field.name,
                name: tempCustomFieldName,
                type: 'per_tickettype',
                fieldType: customFieldType,
                fieldlistID: fieldListID ?? null,
                grouping: 'tickettype'
            };
            if( tempOrderData.customfields_data && tempOrderData.customfields_data[tempCustomField.eventid] && tempOrderData.customfields_data[tempCustomField.eventid][tempCustomFieldName] ) {
              tempCustomField.value = tempOrderData.customfields_data[tempCustomField.eventid][tempCustomFieldName].value;
            }
            if(tempCustomField.value == '' && customFieldType == 'checkbox'){
              tempCustomField.value = false;
            }
            if( !tempCustomFieldOrderData[tempCustomField.eventid] ) {
              tempCustomFieldOrderData[tempCustomField.eventid] = {};
            }
            console.log('Setting custom field for event', tempCustomField.eventid, 'with name', tempCustomFieldName, ':', tempCustomField);
            tempCustomFieldOrderData[tempCustomField.eventid][tempCustomFieldName] = tempCustomField;
          });

          //Per ticket fields
          customfields.value.custom_fields_perticket.forEach(field => {
            console.log("----------------- customfields.value.custom_fields_perticket field -------------------", field);
            if(!customfields_ticket_count[field.name.toString()]){
              customfields_ticket_count[field.name.toString()] = 0;
            }
            if( !field.event_id || field.event_id.toString() != basketItem.id.toString() ) {
              return;
            }
            if(field.tickets && field.tickets.length > 0) {
              const tempTicketId = field.tickets.find((id: number) => id == ticket.id);
              if(!tempTicketId) {
                console.log('Ticket id', ticket.id, 'not found in field.tickets for field', field.name, ':', field.tickets);
                customfields_ticket_count[field.name.toString()] += ticket.count; // advance past all instances of this ticket type
                console.log('Incrementing customfields_ticket_count for field', field.name, 'to', customfields_ticket_count[field.name.toString()]);
                return;
              }
            }
            for(let i = 0; i < ticket.count; i++ ) {
              const tempCustomFieldName = 'T' + customfields_ticket_count[field.name.toString()] + '_' + field.name;
              customfields_ticket_count[field.name.toString()]++;
              const customFieldType = getCustomFieldFieldType(field, ticket, customfields.value.fieldlists || {});
              const fieldListID = getFieldlistID(field, ticket);
              const actual_iteration = i + 1;
              const tempCustomField: CustomFieldOrderDataEntry = {
                  eventid: field.event_id.toString(),
                  ticketID: ticket.id,
                  value: '',
                  'must-be-checked': field.must_be_checked,
                  fieldName: field.name,
                  name: tempCustomFieldName,
                  type: 'per_ticket',
                  fieldType: customFieldType,
                  fieldlistID: fieldListID ?? null,
                  grouping: '#' + actual_iteration
              };
              if( tempOrderData.customfields_data && tempOrderData.customfields_data[tempCustomField.eventid] && tempOrderData.customfields_data[tempCustomField.eventid][tempCustomFieldName] ) {
                tempCustomField.value = tempOrderData.customfields_data[tempCustomField.eventid][tempCustomFieldName].value;
              }
              if(tempCustomField.value == '' && customFieldType == 'checkbox'){
                tempCustomField.value = false;
              }
              if( !tempCustomFieldOrderData[tempCustomField.eventid] ) {
                tempCustomFieldOrderData[tempCustomField.eventid] = {};
              }
              console.log('Setting custom field for event', tempCustomField.eventid, 'with name', tempCustomFieldName, ':', tempCustomField);
              tempCustomFieldOrderData[tempCustomField.eventid][tempCustomFieldName] = tempCustomField;
            }
          });
        });
      });
      currentOrderData.value.customfields_data = tempCustomFieldOrderData;


    }
    console.log('Custom field data set:', currentOrderData.value.customfields_data);
  }

  function getCustomFieldFieldType(customField: CustomField, ticket: Ticket | null, fieldLists: Record<string, FieldListItem>): string {
    let fieldtype = "textfield";
    if(customField.datefield) {
      fieldtype = "datefield";
    }
    else if(customField.text_large) {
      fieldtype = "textarea";
    }
    else if(customField.checkbox) {
      fieldtype = "checkbox";
    }
    else if(customField.cprnumber) {
      fieldtype = "cprnumber";
    }
    else if(customField.fieldlist != undefined ) {
      const fieldListID = getFieldlistID( customField, ticket );

      if( fieldLists && fieldListID != undefined && fieldLists[fieldListID.toString()] && fieldLists[fieldListID.toString()].dropdown ) {
        fieldtype = "dropdown";
      }
    }
    return fieldtype;
  }

  function getFieldlistID(customField: CustomField, ticket: Ticket | null): number | undefined {
    let fieldlistID: number | number[] | undefined = undefined;
    if( customField.fieldlist != undefined ) {
      if( typeof customField.fieldlist == 'number' || ticket == null ) {
        fieldlistID = customField.fieldlist;
      }
      else if(ticket != null ){
        fieldlistID = customField.fieldlist[ticket.id];
      }
    }
    return fieldlistID as number | undefined;
  }

  function setBasketData(data: BasketData) {
    //iterate through basketData.basket and fill in image, venuename and date from events in general store

    // Build per-ticket count snapshot from old basket for analytics comparison
    const prevTicketCounts = new Map<number, { count: number; eventId: number | string }>();
    if (basketData.basket) {
      for (const item of basketData.basket) {
        for (const ticket of item.tickets) {
          prevTicketCounts.set(ticket.id, { count: ticket.count, eventId: item.id });
        }
      }
    }

    basketData = data;

    // Coerce all numeric fields from the backend (API/localStorage/websocket may return strings)
    basketData.basket = basketData.basket?.map(item => mapDtoToCartItem(item as any)) ?? [];

    // Per-ticket add/remove analytics tracking
    if (!receiptCart.value) {
      for (const item of basketData.basket) {
        for (const ticket of item.tickets) {
          const prev = prevTicketCounts.get(ticket.id);
          const oldCount = prev?.count ?? 0;
          const newCount = ticket.count;
          const diff = newCount - oldCount;

          if (diff > 0) {
            trackAddToCart(item, ticket, diff);
          } else if (diff < 0) {
            trackRemoveFromCart(item, ticket, Math.abs(diff));
          }
        }
      }
      // Check for tickets that were completely removed (in old basket but not in new)
      for (const [ticketId, prev] of prevTicketCounts) {
        const stillExists = basketData.basket.some(item =>
          item.tickets.some(t => t.id === ticketId),
        );
        if (!stillExists && prev.count > 0) {
          // Find the ticket data from the old basket to pass to the tracker
          // We need the CartItem context — find from old data reference
          // Since basketData was overwritten, we use a minimal fallback
          const oldItem = data.basket?.find(i =>
            i.tickets?.some((t: { id: number }) => t.id === ticketId),
          );
          if (oldItem) {
            const oldTicket = oldItem.tickets.find((t: { id: number }) => t.id === ticketId);
            if (oldTicket) {
              trackRemoveFromCart(
                mapDtoToCartItem(oldItem as any),
                oldTicket as unknown as import('@/types').Ticket,
                prev.count,
              );
            }
          }
        }
      }
    }

    const startExpireBasket = addEventDataToBasket();
    //We want to reset the cart expiration timer only if we are not in receipt mode
    if(!receiptCart.value && !paymentFailedCart.value && startExpireBasket) {
      if (cartExpireInterval) clearInterval(cartExpireInterval);
      cartExpireInterval = window.setInterval(() => {
        const now = Date.now() / 1000;
        if (basketData.expire && basketData.expire > 0 && now > basketData.expire) {
          doBasketExpireTimeout();
        }
      }, 1000);
      cartExpire.value = basketData.expire ?? 0;
      cardTypes.value = basketData.cardtypes;
      console.log('Cart expire set to:', cartExpire.value);
    }
    else {
      // In receipt mode, payment failed mode, or no tickets reserved, do not start expiration timer and clear it
      if (cartExpireInterval) {
        clearInterval(cartExpireInterval);
        cartExpireInterval = undefined;
      }
      cartExpire.value = 0;
    }

    // console.log('Basket data set:', basketData);
    // console.log('Basket items:', basketData.basket);
  }

  function doBasketExpireTimeout() {
    //clear basket, close connection, goto frontpage
    // console.log('Cart expired, emptying cart and redirecting to frontpage');
    localStorage.setItem('showEmptiedCartToast', 'true');
    clearStoredCartData();
    currentOrderData.value = emptyOrderData;
    basketData.basket = [];
    items.splice(0, items.length);
    cartExpire.value = 0;
    if (cartExpireInterval) clearInterval(cartExpireInterval);
    cartExpireInterval = undefined;
    emptyCart(false);
    websocketStore.close();
    navigateAfterBasketTimeout();
  }

  /*function getBasketData() {
    return basketData;
  }*/

  function addEventDataToBasket() {
    const basket: CartItem[] = [];
    let startExpireBasket = false;

    basketData.basket.forEach(item => {

      const cartItem: CartItem = item;

      basket.push(cartItem);

      if( item.tickets.some(ticket => ticket.count > 0) ) {
        startExpireBasket = true;
      }
    });

    //If in checkout/tickets and basket is empty we must add the checkoutID from checkoutStore
    if(router.currentRoute.value.name == 'checkout-tickets' && basket.length == 0 ) {
      const checkoutID = checkoutStore.getCheckoutID();
      if(checkoutID) {
        const events = generalStore.events();
        const item = events.find(item => item.id == checkoutID);
        if (item && !basket.some(cartItem => cartItem.id == item.id)) {
          basket.push(eventToCartItem(item));
        }
      }
    }

    basketData.basket = basket;

    return startExpireBasket
  }

  // Confirm payment and set receipt data, then empty cart
  function setReceiptConfirmPayment(data: ReceiptConfirmPayment) {
    ReceiptConfirmPayment.value = {
      ...data,
      ts: normalizeUnixTimestamp(data.ts),
      ts_gmt_offset: normalizeUnixTimestamp(data.ts_gmt_offset),
    };
    ReceiptConfirmPayment.value.fee = Number(data.fee) || 0;
    paymentConfirmed.value = ReceiptConfirmPayment.value.ok;
    calculateTotals();
    console.log('Receipt confirm payment set:', ReceiptConfirmPayment);
  }

  function setReceiptOrder(data: Order) {
    receiptOrder.value = {
      ...data,
      ts: normalizeUnixTimestamp(data.ts),
      ts_gmt_offset: normalizeUnixTimestamp(data.ts_gmt_offset),
    };
    receiptOrder.value.dankort_discount = Number(data.dankort_discount) || 0;
    pdfLink.value = receiptOrder.value.mailid && receiptOrder.value.mailid.length > 0 ? '/pdf?id=' + receiptOrder.value.orderid + '&mailid=' + receiptOrder.value.mailid : '#';
    if( receiptOrder.value.basket ) {
      receiptCart.value = true;
      basketData.basket = receiptOrder.value.basket.map(item => mapDtoToCartItem(item as any));
      addEventDataToBasket();
      // Preserve client-only flexData (seat labels) before replacing items.
      // The server basket may not include flexData; without this, seat labels
      // written by applySeatsToCartItems (or restored from localStorage) would
      // be lost when the receipt basket arrives.
      const prevFlexData: Record<string, Record<number, string[] | undefined>> = {};
      for (const item of items) {
        for (const ticket of item.tickets) {
          if (ticket.flexData && ticket.flexData.length > 0) {
            (prevFlexData[item.id] ??= {})[ticket.id] = ticket.flexData;
          }
        }
      }
      items.splice(0, items.length, ...basketData.basket);
      // Restore preserved flexData onto new items (only when the server
      // didn't already provide flexData on the ticket).
      for (const item of items) {
        const ticketMap = prevFlexData[item.id];
        if (!ticketMap) continue;
        for (const ticket of item.tickets) {
          if (!ticket.flexData && ticketMap[ticket.id]) {
            ticket.flexData = ticketMap[ticket.id];
          }
        }
      }
      // If no flexData was found (neither from the server nor from preserved
      // items — e.g. the empty basket message already wiped them before receipt
      // arrived), restore from the dedicated localStorage snapshot saved before
      // payment.
      const hasAnyFlexData = items.some(item => item.tickets.some(t => t.flexData && t.flexData.length > 0));
      if (!hasAnyFlexData) {
        const stored = loadStoredFlexData();
        if (stored) {
          for (const item of items) {
            const ticketMap = stored[item.id];
            if (!ticketMap) continue;
            for (const ticket of item.tickets) {
              if (ticketMap[ticket.id]) {
                ticket.flexData = ticketMap[ticket.id];
              }
            }
          }
        }
      }
      clearStoredFlexData();
      console.log('Items after update:', items);
      calculateTotals();
      console.log('Cart totals after update:', cartTotals.value);
      setReceiptURL();

      if(Number.isFinite(receiptOrder.value.ts)){
        ReceiptConfirmPayment.value.ts = receiptOrder.value.ts;
        ReceiptConfirmPayment.value.ts_gmt_offset = receiptOrder.value.ts_gmt_offset;
      }
    }
    currentOrderData.value = emptyOrderData;
    clearUnpaidOrderData();
    clearStoredCartData();
    removeAllGiftcards();
    console.log('Receipt order set:', receiptOrder);
  }

  function setAPIReceipt(data: APIReceipt) {
    console.log('API Receipt begin:');
    receiptCart.value = true;

    function execute() {
      const tempReceiptConfirmPayment: ReceiptConfirmPayment = {
        cardnumber: data.cardnumber,
        currency: data.currency,
        fee: data.fee,
        giftcards: data.giftcards,
        mobile_pay_info: '',
        ok: data.status == 'ok',
        orderid: data.orderid,
        transaction: data.transaction,
        ts: data.ts,
        ts_gmt_offset: data.ts_gmt_offset,
        passbook: data.passbook ?? emptyPassbookData
      };
      setReceiptConfirmPayment(tempReceiptConfirmPayment);
      const tempReceiptOrder: Order = {
        status: data.status,
        orderid: data.orderid,
        ts: data.ts,
        ts_gmt_offset: data.ts_gmt_offset,
        mailid: data.mailid,
        basket: data.basket,
        info: data.info,
        payment_id: '',
        payment_vars: {},
        dankort_discount: data.dankort_discount,
        giftcards: data.giftcards,
        additional_receipt_message: data.additional_receipt_message ?? '',
        hide_receipt_download_button: data.hide_receipt_download_button,
      };
      setReceiptOrder(tempReceiptOrder);
      pdfReady.value = true;
      console.log('API Receipt set:', data);
    }

    if (!generalStore.loading && generalStore.general != null) {
      execute();
    } else {
      console.log('API Receipt: waiting for generalStore to finish loading...');
      const stop = watch(
        () => generalStore.loading,
        (loading) => {
          if (!loading && generalStore.general != null) {
            stop();
            execute();
          }
        }
      );
    }
  }

  // Set url to receipt with orderid and mailid
  function setReceiptURL() {
    if( receiptOrder.value.mailid && receiptOrder.value.mailid.length > 0  && receiptOrder.value.orderid > 0 ) {
      const params = '?orderid=' + receiptOrder.value.orderid + '&mailid=' + receiptOrder.value.mailid;
      const basePath = window.location.pathname.startsWith('/doorsale') ? '/doorsale/receipt' : '/receipt';
      history.replaceState({}, '', basePath + params);
    }
  }

  const isOpen = ref(false);

  const router = useRouter();
  const route = useRoute();

  const open = () => {
    isOpen.value = true;
  };

  const close = () => {
    isOpen.value = false;
  };

  function selectTickets(
    eventId: number | string | undefined,
    ticketId: number,
    count: number = 1
  ) {
    console.log('Selecting tickets with eventId:', eventId, 'ticketId:', ticketId, 'count:', count);

    // Any change to ticket counts invalidates previously confirmed seat price
    // assignments and stored seatmap selections (the user must re-select seats
    // for the new ticket count).
    confirmedSeatPrices.value = {};
    seatmapStore.clearSeatmapState();

    const reserveTickets: Record<number, number> = {};
    // console.log('eventItem:', eventItem);
    let foundTicket = false;

    items.forEach(item => {
      item.tickets.forEach(ticket => {
        if (ticket.count > 0) {
          if( ticket.id == ticketId ) {
            ticket.count = count;
            foundTicket = true;
          }
          reserveTickets[ticket.id] = ticket.count;
        }
      });
    });
    if( !foundTicket ) {
      reserveTickets[ticketId] = count;
    }
    // console.log('Current reserved tickets before update:', reserveTickets);

    /* Remove any tickets with count 0 */

    for (const ticketId in reserveTickets) {
      if (reserveTickets[ticketId] == 0) {
        delete reserveTickets[ticketId];
      }
    }

    // // console.log('Reserve tickets flag:', reserveTickets);
    if( Object.keys(reserveTickets).length > 0 ) {
      websocketStore.send('reservetickets', { tickets: reserveTickets, is_doorsale: doorsaleStore.isActive }, undefined);
    }
    else {
      emptyCart(true);
    }
    // console.log('Cart items after selecting tickets:', items);
  }

  function setSimpleBasketData(data: SimpleBasketData) {
    console.log("cart.setSimpleBasketData()");

    //Make basketData from SimpleBasketData
    //Then set via updateReservedTickets
    if(data.timeout != undefined && data.timeout == 1 ) {
      doBasketExpireTimeout();
    }
    else if(!receiptCart.value && !paymentFailedCart.value && !router.currentRoute.value.name?.toString().startsWith('checkout-receipt')) {
      console.log('-- Setting simple basket data:', data);
      console.log('-- receiiptCart.value:', receiptCart.value);
      console.log('-- paymentFailedCart.value:', paymentFailedCart.value);
      basketData.expire = data.expire;
      basketData.basket = data.basket;
      updateReservedTickets(basketData);
    }
    else {
      // console.log("Do nothing?");
      // console.log('receiptCart.value:', receiptCart.value);
      // console.log('paymentFailedCart.value:', paymentFailedCart.value);
      // console.log('Current route:', router.currentRoute.value.name);
    }
  }

  function updateReservedTickets(messageData: BasketData) {
    setCustomFieldData(messageData);
    setBasketData(messageData);

    if (messageData.error && messageData.error.startsWith('limit_order_by_userdefined_id_')) {
      const toastStore = useToastStore();
      const { t } = i18n.global;
      toastStore.showToast(t(`checkout.${messageData.error}`), 15000);
    }

    // Preserve client-only flexData (seat labels) before replacing items.
    // The server basket never includes flexData; without this, navigating to
    // the information view (which triggers startpurchase → basket) would wipe
    // the seat labels written by applySeatsToCartItems.
    const prevFlexData: Record<string, Record<number, string[] | undefined>> = {};
    for (const item of items) {
      for (const ticket of item.tickets) {
        if (ticket.flexData && ticket.flexData.length > 0) {
          (prevFlexData[item.id] ??= {})[ticket.id] = ticket.flexData;
        }
      }
    }
    // Update items with new basket data.
    items.splice(0, items.length, ...basketData.basket);
    // Restore preserved flexData onto the new items.
    for (const item of items) {
      const ticketMap = prevFlexData[item.id];
      if (!ticketMap) continue;
      for (const ticket of item.tickets) {
        if (ticketMap[ticket.id]) {
          ticket.flexData = ticketMap[ticket.id];
        }
      }
    }
    console.log('Items after update:', items);
    checkForSeatmaps();
    calculateTotals();
    updateCurrentCount();
    if( messageData.different_count_message ) {
      differentCountMessage.value = messageData.different_count_message;
    }
    else {
      differentCountMessage.value = { message: 'initial', count: 0 };
    }
    storeCartData();

    // Mark in-flight request as complete and flush any changes queued while we were waiting
    reservationInFlight.value = false;
    if (Object.keys(pendingChanges.value).length > 0) {
      clearTimeout(flushTimer);
      flushTimer = window.setTimeout(() => {
        flushPendingChanges();
      }, 100);
    }
  }

  function checkForSeatmaps() {

    seatmapActive.value = false; // for routing
    //Iterate through items, then load seatmap for any tickets that have a seatmap in checkoutStore.tickets
    items.forEach(item => {
      item.tickets.forEach(ticket => {
        checkoutStore.tickets.forEach(checkoutTicket => {
          if( checkoutTicket.id == ticket.id && checkoutTicket.seatmap ) {
            seatmapStore.loadSeatmap(checkoutTicket.seatmap);
            seatmapActive.value = true;
          }
        });
      });
    });
  }

  function resendReservedTickets() {
    const reserveTickets: Record<number, number> = {};
    items.forEach(item => {
      item.tickets.forEach(ticket => {
        if (ticket.count > 0) {
          reserveTickets[ticket.id] = ticket.count;
        }
      });
    });
    const olCustomer = checkoutStore.getUrlParam('OL_Customer');
    websocketStore.send('reservetickets', { tickets: reserveTickets, order_limit_customer_id: olCustomer }, undefined);
  }

  function calculateTotals() {
    const totals = {
      total: 0,
      fee: 0,
      vat: 0,
      price: 0,
      discount: 0,
      transaction_fee: 0,
    };

    let ticketprice_vat_percentage = 0;
    let ticketfee_vat_percentage = 0;
    let cardfee_vat_percentage = 0;

    items.forEach(item => {
      //get PG info here for seatmap - do later, see if we cannot do it more efficiently
      //current function in SFTNG.Ticketsale.Basket.js - populate_ticketsale
      ticketprice_vat_percentage = (1 - (1 / (1 +item.vat.ticketprice / 100)));
      ticketfee_vat_percentage = (1 - (1 / (1 +item.vat.ticketfee / 100)));
      cardfee_vat_percentage = (1 - (1 / (1 +item.vat.cardfee / 100)));
      item.tickets.forEach(ticket => {
        const hasPriceGroups = (ticket.price_groups?.length ?? 0) > 0;
        const confirmedTotal = hasPriceGroups ? (confirmedSeatPrices.value[ticket.id] ?? null) : null;
        const effectivePricePerTicket = hasPriceGroups
          ? (confirmedTotal != null
              ? Math.round(confirmedTotal / ticket.count)
              : ticket.min_price)
          : ticket.price;
        const ticketfee_extra = ticket.hide_ticketfee_extra
          ? 0
          : effectivePricePerTicket * ticket.count * ticket.ticketfee_extra;
        const ticketfee = ticket.ticketfee * ticket.count;
        const ticketprice = confirmedTotal != null ? confirmedTotal : effectivePricePerTicket * ticket.count;
        const vat_ticketprice = ticketprice_vat_percentage * ticketprice;
        const vat_ticketfee = ticketfee_vat_percentage * (ticketfee + ticketfee_extra);
        totals.vat += vat_ticketprice + vat_ticketfee;
        totals.price += ticketprice;
        totals.total += ticketprice + ticketfee + ticketfee_extra;
        totals.fee += ticketfee + ticketfee_extra;
      });

      item.upsaleTickets?.forEach(upsaleTicket => {
        totals.total += upsaleTicket.price * upsaleTicket.count;
      });
    });

    // We might want to use this.
    /*if(basketData.dankort_discount && basketData.dankort_discount > 0 ) {
      totals.discount = basketData.dankort_discount;
      totals.total -= basketData.dankort_discount;
    }
    else */
    if(receiptOrder.value && receiptOrder.value.dankort_discount && receiptOrder.value.dankort_discount > 0 ) {
      totals.discount = receiptOrder.value.dankort_discount;
      totals.total -= receiptOrder.value.dankort_discount;
    }

    if(ReceiptConfirmPayment.value && ReceiptConfirmPayment.value.fee && ReceiptConfirmPayment.value.fee > 0) {
      totals.transaction_fee = ReceiptConfirmPayment.value.fee;
      totals.total += ReceiptConfirmPayment.value.fee;
    }

    const vat_transaction_fee = totals.transaction_fee * cardfee_vat_percentage;
    totals.vat += vat_transaction_fee;

    const vat_discount = totals.discount * ticketfee_vat_percentage;
    totals.vat -= vat_discount;

    // Giftcard deductions — use server data on receipt, client state otherwise
    let giftcardDiscount = 0;
    let remaining = totals.total;

    if (receiptCart.value && ReceiptConfirmPayment.value.giftcards && ReceiptConfirmPayment.value.giftcards.length > 0) {
      // Server sends negative balance for deductions
      for (const gc of ReceiptConfirmPayment.value.giftcards) {
        const deduction = Math.min(Math.abs(gc.balance), remaining);
        giftcardDiscount += deduction;
        remaining -= deduction;
        if (remaining <= 0) break;
      }
    } else if (receiptCart.value && receiptOrder.value.giftcards && receiptOrder.value.giftcards.length > 0) {
      for (const gc of receiptOrder.value.giftcards) {
        const deduction = Math.min(Math.abs(gc.balance), remaining);
        giftcardDiscount += deduction;
        remaining -= deduction;
        if (remaining <= 0) break;
      }
    } else {
      for (const gc of giftcards.value) {
        const deduction = Math.min(gc.balance, remaining);
        giftcardDiscount += deduction;
        remaining -= deduction;
        if (remaining <= 0) break;
      }
    }

    cartTotals.value.total = totals.total - giftcardDiscount;
    cartTotals.value.total_price = totals.price;
    cartTotals.value.total_ticketfee = totals.fee;
    cartTotals.value.total_vat = totals.vat;
    cartTotals.value.total_discount = totals.discount;
    cartTotals.value.total_transaction_fee = totals.transaction_fee;
    cartTotals.value.total_giftcard_discount = giftcardDiscount;
  }

  //Compare the count in basketData with currentCount and update currentCount with new count. If in checkout we want to trigger differentCount warning (Warning is in vue file, boolean flag here)
  function updateCurrentCount(){
    const newCount: Record<string, number> = {};
    differentCount.value = false;
    items.forEach(item => {
      item.tickets.forEach(ticket => {
        const count = Number(ticket.count) || 0;
        newCount[ticket.id] = count;
        // Skip tickets that have a pending change queued — the user has already
        // expressed a newer intent that hasn't been sent yet.
        if (pendingChanges.value[ticket.id] != undefined) {
          return;
        }
        if( currentCount.value[ticket.id] != undefined && currentCount.value[ticket.id] != count ) {
          if(currentCount.value[ticket.id] != 0) {
            differentCount.value = true;
          }
          currentCount.value[ticket.id] = count;
        }
      });
    });
    //If currentCount has any keys that are not in newCount we must set them to 0
    for( const ticketId in currentCount.value ) {
      if( newCount[ticketId] == undefined && pendingChanges.value[Number(ticketId)] == undefined ) {
        if( currentCount.value[ticketId] != 0 ) {
          differentCount.value = true;
        }
        currentCount.value[ticketId] = 0;
      }
    }
    //if we are not on checkout-tickets or checkout-upsale page we want to reset differentCount to false, since we only want to show the warning on those pages
    const routeName = router.currentRoute.value.name?.toString();
    if( !routeName?.startsWith('checkout-tickets') && !routeName?.startsWith('checkout-upsale') ) {
      differentCount.value = false;
    }
  }

  function emptyCart(excemptActiveEventID: boolean = false) {
    const exemptID = excemptActiveEventID ? checkoutStore.getCheckoutID() : null;
    for( const ticketId in currentCount.value ) {
      currentCount.value[ticketId] = 0;
    }
    if (exemptID) {
      for (let i = items.length - 1; i >= 0; i--) {
        if (items[i].id != exemptID) {
          items.splice(i, 1);
        }
      }
    }
    else {
      items.splice(0, items.length);
    }
    differentCount.value = false;
    //clearStoredCartData();
    console.log('Cart emptied, sending emptybasket message to server');
    websocketStore.send('emptybasket', {  }, undefined);
  }

  function removeOrphanedUpsaleTickets() {
    // Collect all active (count > 0) non-upsale tickets across all cart items
    const activeRegularTickets = items.flatMap(item =>
      item.tickets.filter(t => !t.upsale_flag && t.count > 0)
    );
    const activeRegularIds = new Set(activeRegularTickets.map(t => Number(t.id)));

    // Build the set of upsale ticket IDs still allowed by active regular tickets
    const allowedUpsaleIds = new Set<number>();
    activeRegularTickets.forEach(ticket => {
      ticket.upsale_available_tickets?.forEach(entry => {
        const hasBanned = entry.banned.some(bannedId => activeRegularIds.has(bannedId));
        if (!hasBanned) {
          allowedUpsaleIds.add(entry.id);
        }
      });
    });

    // Zero out any active upsale tickets that are no longer allowed
    items.forEach(item => {
      item.tickets.filter(t => t.upsale_flag && t.count > 0).forEach(ticket => {
        if (!allowedUpsaleIds.has(Number(ticket.id))) {
          currentCount.value[ticket.id] = 0;
          ticket.count = 0;
        }
      });
    });

    // Remove cart items where all tickets now have count 0
    for (let i = items.length - 1; i >= 0; i--) {
      if (!items[i].tickets.some(t => t.count > 0)) {
        items.splice(i, 1);
      }
    }
  }

  function removeFromCart(id: number | string, resend: boolean = true) {
    const index = items.findIndex(item => item.id == id);
    const eventId = items[index].id;
    items[index].tickets.forEach(ticket => {
      currentCount.value[ticket.id] = 0;
    });
    if (index != -1) {
      items.splice(index, 1);
    }
    removeOrphanedUpsaleTickets();
    if (resend) {
      if( items.length == 0 ) {
        emptyCart(false);
      }
      else {
        websocketStore.send('subscribeevent', { id: eventId, is_doorsale: doorsaleStore.isActive }, undefined);
        resendReservedTickets();
      }
    }
  }

  function removeTypeFromCart(id: number | string, type: 'ticket' | 'upsaleTicket', resend: boolean = true) {
    const index = items.findIndex(item => item.id == id);
    console.log('removeTypeFromCart() called with id:', id, 'type:', type, 'resend:', resend);
    if (index == -1) return;
    const eventId = items[index].id;
    console.log('Found item with eventId:', eventId, 'and tickets:', items[index].tickets);

    if (type == 'ticket') {
      items[index].tickets.filter(t => !t.upsale_flag).forEach(ticket => {
        currentCount.value[ticket.id] = 0;
        ticket.count = 0;
      });
      removeOrphanedUpsaleTickets();
    }
    else if (type == 'upsaleTicket') {
      items[index].tickets.filter(t => t.upsale_flag).forEach(ticket => {
        currentCount.value[ticket.id] = 0;
        ticket.count = 0;
      });
    }

    // Re-check index validity since removeOrphanedUpsaleTickets may have spliced items
    const currentIndex = items.findIndex(item => item.id == id);
    console.log('Current index after removing type:', currentIndex);
    if (currentIndex != -1){
      const hasAnyTickets = items[currentIndex].tickets.some(t => t.count > 0);
      if (!hasAnyTickets) {
        items.splice(currentIndex, 1);
      }
    }


    console.log('Cart items after removeTypeFromCart:', items);

    if (resend) {
      if (items.length == 0) {
        emptyCart(false);
      }
      else {
        websocketStore.send('subscribeevent', { id: eventId, is_doorsale: doorsaleStore.isActive }, undefined);
        resendReservedTickets();
      }
    }
  }

  function gotoTicketReservation(id: number | string) {
    const index = items.findIndex(item => item.id == id);
    const eventId = items[index].id;
    const eventName = items[index].name;
    const isClosedEvent = typeof eventId === 'string' && /^[0-9a-f]{96}$/.test(eventId);
    checkoutStore.setCheckoutID(eventId);
    if (route.name?.toString().startsWith('doorsale-')) {
      router.push({ name: 'doorsale-tickets', params: { eventId } });
    } else {
      router.push({
        name: 'checkout-tickets',
        params: isClosedEvent
          ? { id: eventId }
          : { id: eventId, name: slugify(eventName) },
      });
    }
    close();
  }

  function gotoUpsaleReservation(id: number | string) {
    const index = items.findIndex(item => item.id == id);
    const eventId = items[index].id;
    checkoutStore.setCheckoutID(eventId);
    if (route.name?.toString().startsWith('doorsale-')) {
      router.push({ name: 'doorsale-upsale', params: { eventId } });
    } else {
      router.push({ name: 'checkout-upsale' });
    }
    close();
  }

  function cleanUpEmptyEvents(resend: boolean = true) {
    for (let i = items.length - 1; i >= 0; i--) {
      const item = items[i];
      const hasTickets = item.tickets.some(ticket => ticket.count > 0);
      if (!hasTickets) {
        removeFromCart(item.id, resend);
      }
    }
  }

  function resetCart() {
    // console.log('cart.resetCart()');
    items.splice(0, items.length);
    cartTotals.value = { total: 0, total_ticketfee: 0, total_price: 0, total_vat: 0, total_discount: 0, total_transaction_fee: 0, total_giftcard_discount: 0 };
    cartExpire.value = 0;
    cardTypes.value = [];
    receiptCart.value = false;
    receiptError.value = null;
    if (cartExpireInterval) {
      clearInterval(cartExpireInterval);
      cartExpireInterval = undefined;
    }
    basketData = {
      basket: [],
      cardtypes: [],
      dankort_discount: 0,
      dankort_discount_max: 0,
      dankort_discount_min: 0,
      error: '',
      different_count_message: undefined,
      expire: 0,
      volume_discounts_in_use: undefined,
      customfields: undefined,
      timeout: undefined,
    };
    currentOrderData.value = emptyOrderData;
    customfields.value = emptyCustomFieldData;
    receiptOrder.value = emptyReceiptOrder;
    ReceiptConfirmPayment.value = emptyReceiptConfirmPayment;
    checkoutStore.clearCheckoutData();
    paymentConfirmed.value = false;
    pdfReady.value = false;
    pdfLink.value = '#';
    confirmedSeatPrices.value = {};
    clearUnpaidOrderData();
    clearStoredCartData();
    clearStoredFlexData();
    seatmapStore.clearSeatmapState();
    seatmapStore.clearEventSeatmapRegistry();
  }

  function isCartEmpty() {
    return items.length == 0;
  }

  function updateCustomFieldsStartPurchase(data: StartPurchaseData) {
    // console.log('cart.updateCustomFieldsStartPurchase():', data);
    basketData.customfields = emptyCustomFieldData;
    basketData.customfields.custom_fields = data.custom_fields ? data.custom_fields : [];
    basketData.customfields.custom_fields_perticket = data.custom_fields_perticket ? data.custom_fields_perticket : [];
    basketData.customfields.custom_fields_pertickettype = data.custom_fields_pertickettype ? data.custom_fields_pertickettype : [];
    basketData.customfields.fieldlists = data.fieldlists ? data.fieldlists : {};
    // console.log('-- Basket data custom fields after start purchase update:', basketData.customfields);
    setCustomFieldData(basketData);
    // console.log('-- Custom fields after start purchase update:', customfields.value);
    // console.log('-- Current order data after start purchase update:', currentOrderData.value);
  }



  watch(currentOrderData, () => {
    storeCartData();
  }, { deep: true });

  //Function to store relevant cart data to localstorage
  function storeCartData() {
    const cartData = {
      basketData: basketData,
      currentOrderData: currentOrderData.value,
      customfields: customfields.value,
      confirmedSeatPrices: confirmedSeatPrices.value,
      ts: Date.now(),
    };
    localStorage.setItem('cartData', JSON.stringify(cartData));
    // console.log('-- Cart data stored to localStorage:', cartData);
  }

  function loadCartData(do_cleanup: boolean = true) {
    const cartDataString = localStorage.getItem('cartData');
    if( cartDataString ) {
      const cartData = JSON.parse(cartDataString);
      //We only load the data if it is less than 20 minutes old. We don't want to store data longer than necessary.
      if( cartData.ts && Date.now() - cartData.ts < 20 * 60 * 1000) {
        if( cartData.basketData ) {
          if(!receiptCart.value || !router.currentRoute.value.name?.toString().startsWith('checkout-receipt')) {
            basketData = cartData.basketData;
            setBasketData(basketData);
            if(receiptCart.value) {
              receiptCart.value = false;
            }
          }
          if( do_cleanup ) {
            console.log('Cleaning up empty events from loaded cart data');
            cleanUpEmptyEvents(false);
          }
          items.splice(0, items.length, ...basketData.basket);
        }
        if( cartData.currentOrderData ) {
          console.log('Loading current order data from localStorage:', cartData.currentOrderData);
          currentOrderData.value = cartData.currentOrderData;
          console.log('Current order data loaded from localStorage:', currentOrderData.value);
        }
        if( cartData.customfields ) {
          customfields.value = cartData.customfields;
        }
        if( cartData.confirmedSeatPrices ) {
          confirmedSeatPrices.value = cartData.confirmedSeatPrices;
        }
      }
    }
    clearStoredCartData();
    loadGiftcards();
  }

  function getAvailableUpsaleTicketsForBasket() {

    const allCartTickets: Ticket[] = [];
    items.forEach(event => {
      if (event.tickets) {
        allCartTickets.push(...event.tickets);
      }
    });

    const availableUpsaleTickets: number[] = [];

    // Build a Set of all ticket ids in the cart for efficient lookup
    const allCartTicketIds = new Set(allCartTickets.map(t => Number(t.id)));

    // run through all the tickets reserved in basket and gather all unique upsale_available_tickets
    allCartTickets.forEach(ticket => {
      ticket.upsale_available_tickets?.forEach(upsale_available_ticket => {
        // Check if any banned ID is present in the cart
        const hasBanned = upsale_available_ticket.banned.some(bannedId => allCartTicketIds.has(bannedId));
        if (hasBanned) {
          // If any banned ID is present in the cart, skip adding this upsale ticket
          return;
        }
        availableUpsaleTickets.push(Number(upsale_available_ticket.id));
      });
    });
    return availableUpsaleTickets;
  }

  function clearStoredCartData() {
    localStorage.removeItem('cartData');
  }

  /**
   * Save a snapshot of all ticket flexData (seat labels) to a dedicated
   * localStorage key. Called from applySeatsToCartItems so the labels survive
   * the seatmap store being cleared on payment and the empty basket message
   * overwriting the main cartData. Expires after 20 minutes.
   */
  function storeFlexData() {
    const data: Record<string | number, Record<number, string[]>> = {};
    let hasAny = false;
    for (const item of items) {
      for (const ticket of item.tickets) {
        if (ticket.flexData && ticket.flexData.length > 0) {
          (data[item.id] ??= {})[ticket.id] = ticket.flexData;
          hasAny = true;
        }
      }
    }
    if (hasAny) {
      localStorage.setItem('seatFlexData', JSON.stringify({ data, ts: Date.now() }));
    }
  }

  function loadStoredFlexData(): Record<string | number, Record<number, string[]>> | null {
    try {
      const raw = localStorage.getItem('seatFlexData');
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (parsed.ts && Date.now() - parsed.ts < 20 * 60 * 1000) {
        return parsed.data;
      }
    } catch { /* ignore */ }
    return null;
  }

  function clearStoredFlexData() {
    localStorage.removeItem('seatFlexData');
  }

  /**
   * Clear flexData (seat labels) on all tickets of a specific event's cart item,
   * and also clear the localStorage flexData snapshot. Called when the user
   * returns to the seatmap selection view so stale labels from a previous
   * selection don't persist through basket updates or navigation.
   */
  function clearFlexDataForEvent(eventId: number | string) {
    const cartItem = items.find(ci => String(ci.id) == String(eventId));
    if (cartItem) {
      for (const ticket of cartItem.tickets) {
        ticket.flexData = undefined;
      }
    }
    clearStoredFlexData();
  }

  /**
   * For every seatmap the customer has completed, read the confirmed seat indices
   * from the seatmap store and write their human-readable labels into
   * `ticket.flexData` on the matching cart item tickets. This populates the data
   * that `CheckoutItemSection` renders when displaying seat information later in
   * the checkout flow.
   *
   * Label distribution: the confirmed seats for a seatmap are assigned to ticket
   * types in `ticketIds` order. Each ticket type consumes as many labels as its
   * `count` field says (i.e. how many individual tickets of that type are in the
   * basket for this event).
   */
  function applySeatsToCartItems() {
    console.log('Applying seats to cart items');
    const checkoutId = checkoutStore.getCheckoutID();
    if (!checkoutId) return;
    console.log('Checkout ID:', checkoutId);
    const cartItem = items.find(ci => ci.id == checkoutId);
    if (!cartItem) return;

    console.log('Cart item for checkout ID:', cartItem);

    for (const queueItem of seatmapStore.seatmapQueue) {
      const seatsOurs = seatmapStore.seatStates[queueItem.name]?.seatsOurs ?? [];
      const assignments = seatmapStore.seatTicketAssignments[queueItem.name];
      console.log(`Processing seatmap queue item: ${queueItem.name}, seatsOurs: ${seatsOurs}, assignments:`, assignments);

      // Count how many seatsOurs are covered by explicit assignments
      let assignedCount = 0;
      if (assignments && Object.keys(assignments).length > 0) {
        // Assignment-aware distribution: group seats by assigned ticketId
        const seatsByTicket: Record<number, number[]> = {};
        console.log('Seat assignments found, applying assignment-aware distribution');
        for (const seat of seatsOurs) {
          const ticketId = assignments[seat];
          if (ticketId != null) {
            (seatsByTicket[ticketId] ??= []).push(seat);
            assignedCount++;
          }
        }
        console.log('Seats grouped by assigned ticketId:', seatsByTicket);
        console.log(`Assigned ${assignedCount} out of ${seatsOurs.length} seats with explicit assignments`);
        if (assignedCount > 0) {
          for (const ticketId of queueItem.ticketIds) {
            const ticket = cartItem.tickets.find(t => t.id == ticketId);
            if (!ticket) continue;
            const seatIndices = seatsByTicket[ticketId] ?? [];
            ticket.flexData = seatIndices.map(idx => seatmapStore.getSeatLabel(idx, queueItem.name));
            console.log(`Ticket ID ${ticketId} assigned seats:`, ticket.flexData);
            // Compute confirmed total price for this ticket type from its price groups.
            if (seatIndices.length > 0) {
              let priceTotal = 0;
              let allResolved = true;
              for (const idx of seatIndices) {
                const pg = seatmapStore.getAssignedSeatPriceGroup(idx, queueItem.name);
                if (pg?.price != null) {
                  priceTotal += pg.price;
                } else {
                  allResolved = false;
                  break;
                }
              }
              if (allResolved) {
                confirmedSeatPrices.value[ticketId] = priceTotal;
              }
            }
          }
        }
      }

      // Fallback: ordinal offset distribution when assignments are absent
      // or stale (none of the current seatsOurs matched the stored assignments).
      if (assignedCount == 0) {
        // Fallback: sequential offset distribution (original behaviour)
        const labels = seatsOurs.map(idx => seatmapStore.getSeatLabel(idx, queueItem.name));
        let labelOffset = 0;
        for (const ticketId of queueItem.ticketIds) {
          const ticket = cartItem.tickets.find(t => t.id == ticketId);
          if (!ticket) continue;
          ticket.flexData = labels.slice(labelOffset, labelOffset + ticket.count);
          labelOffset += ticket.count;
        }
      }
    }
    storeCartData();
    storeFlexData();
  }

  return {
    removeFromCart,
    removeTypeFromCart,
    gotoTicketReservation,
    gotoUpsaleReservation,
    items,
    selectTickets,
    updateReservedTickets,
    resendReservedTickets,
    isOpen,
    open,
    close,
    emptyCart,
    cleanUpEmptyEvents,
    cartExpire,
    cartTotals,
    cardTypes,
    receiptCart,
    receiptError,
    resetCart,
    setReceiptConfirmPayment,
    setSimpleBasketData,
    setReceiptOrder,
    receiptOrder,
    ReceiptConfirmPayment,
    ReceiptConfirmPaymentsSent,
    paymentConfirmed,
    pdfReady,
    pdfLink,
    customfields,
    currentOrderData,
    setCustomFieldRule,
    getCustomFieldRule,
    setAPIReceipt,
    setReceiptURL,
    isCartEmpty,
    updateCustomFieldsStartPurchase,
    storeCartData,
    loadCartData,
    getAvailableUpsaleTicketsForBasket,
    clearStoredCartData,
    currentCount,
    differentCount,
    differentCountMessage,
    queueTicketChange,
    setUnpaidOrder,
    cancelUnpaidOrder,
    unpaidOrder,
    paymentFailedTimeoutFlag,
    paymentFailedExpire,
    paymentFailedAvailable,
    doBackupUnpaidOrder,
    ignoreCancelPaymentModal,
    firstBasketLoad,
    seatmapActive,
    applySeatsToCartItems,
    confirmedSeatPrices,
    clearFlexDataForEvent,
    giftcards,
    addGiftcard,
    removeGiftcard,
    removeAllGiftcards,
    giftcardAlreadyAdded,
    getGiftcardVat,
    giftcardAllowed,
    getGiftcardInfo,
    loadGiftcards,
  };
});
